import { CmpApi } from '@iabtechlabtcf/cmpapi';
import { TCModel, TCString, GVL } from '@iabtechlabtcf/core';
import gvlData from './fixtures/iab-gvl-v3.json';
import { getSupportedIABVendorIds } from './vendors';
import { getConsentCookie } from '../cookies';

// id for locally testing only, using Allegro's CMP_ID
const CMP_ID = 220;
const CMP_VERSION = 1;
const TC_STRING_COOKIE_NAME = 'mrTCString';
let _TC_MODEL = null;
let _cmpApi = null;
let _gvl = null;
let _cmpEventListenerId = null;

// receive TCF v2 compliant data structures in the event listener callbacks
export const TCF_API_VERSION = 2;

export const CMP_EVENTS = {
  NEW: 'newOrResetTCString',
  UPDATE: 'cmpInvokedForUpdate',
  COMPLETE: 'cmpActionComplete',
};

/**
 * Initialize IAB TCF v2 API, must be called before any __tcfapi calls.
 *
 * Which will trigger any registered user-land listeners via:
 * window.__tcfapi('addEventListener', TCF_API_VERSION, (tcdata, success) => { ... });
 *
 * CORE DOCS (gvl, tcmodel, tcstring)
 * @see https://github.com/InteractiveAdvertisingBureau/iabtcf-es/tree/master/modules/core
 *
 * CMP API DOCS (events)
 * @see https://github.com/InteractiveAdvertisingBureau/iabtcf-es/tree/master/modules/cmpapi
 */
export async function initializeTCF() {
  // client side only
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const gvl = new GVL(gvlData);
    await gvl.readyPromise;

    // restrict to MR supported vendors
    gvl.narrowVendorsTo(getSupportedIABVendorIds());
    _gvl = gvl;

    const tcStringCookie = getTCStringFromCookie();

    if (tcStringCookie) {
      _TC_MODEL = TCString.decode(tcStringCookie);
      _TC_MODEL.gvl = gvl;
    } else {
      _TC_MODEL = new TCModel(gvl);
      _TC_MODEL.cmpId = CMP_ID;
      _TC_MODEL.cmpVersion = CMP_VERSION;
      _TC_MODEL.consentScreen = 1;
      _TC_MODEL.consentLanguage = 'EN';
      _TC_MODEL.isServiceSpecific = false;
      _TC_MODEL.useNonStandardStacks = false;
      _TC_MODEL.publisherCountryCode = 'US';
    }

    _cmpApi = new CmpApi(CMP_ID, CMP_VERSION, false, {
      // middleware if we need it in the future to modify TC Model data
      // before it returns to event listeners. Currently just logging for errors.
      getTCData: (next, tcData, status) => {
        try {
          next(tcData, status);
        } catch (error) {
          console.error('MC CMP - Error in getTCData middleware:', error);
          next(null, false);
        }
      },
    });

    console.log('MC CMP - Initialized IAB TCF window.__tcfapi');

    // debug logs
    if (_cmpEventListenerId) {
      removeCmpEventListener(_cmpEventListenerId);
      _cmpEventListenerId = null;
    }

    registerCmpEventListener();
    // end debug logs

    // fires the event with status tcloaded
    if (tcStringCookie) {
      triggerCmpEvent(CMP_EVENTS.COMPLETE);
    }
  } catch (e) {
    console.error('MC CMP - Error initializing IAB TCF:', e);
  }
}

export function getTCModel() {
  return _TC_MODEL;
}

export function getCmpApi() {
  return _cmpApi;
}

export function getGVL() {
  return _gvl;
}

export function generateTCStringFromModel() {
  const tcModel = getTCModel();

  if (!tcModel) {
    return null;
  }

  return TCString.encode(tcModel);
}

/**
 * @see https://github.com/InteractiveAdvertisingBureau/iabtcf-es/tree/master/modules/cmpapi
 */
export function triggerCmpEvent(eventName) {
  const supportedEvents = [CMP_EVENTS.NEW, CMP_EVENTS.UPDATE, CMP_EVENTS.COMPLETE];
  const cmpApi = getCmpApi();

  if (!_cmpApi || !supportedEvents.includes(eventName)) {
    console.error(`MC CMP - Uninitialized cmpApi unsupported or event: ${eventName}`);
    return;
  }

  // show cmp ui for the very first time
  // tcData.eventStatus: "tcloaded"
  if (eventName === CMP_EVENTS.NEW) {
    const cmpVisible = true;
    cmpApi.update('', cmpVisible);
  }

  // show cmp ui for user to change consents
  // tcData.eventStatus: "cmpuishown"
  if (eventName === CMP_EVENTS.UPDATE) {
    const tcString = generateTCStringFromModel();
    const cmpVisible = true;

    cmpApi.update(tcString, cmpVisible);
  }

  // user action completed OR page reloaded with existing TC String
  // - tcData.eventStatus: "useractioncomplete" (user clicked CMP CTAs)
  // - tcData.eventStatus: "tcloaded" (page reloaded)
  if (eventName === CMP_EVENTS.COMPLETE) {
    const tcString = generateTCStringFromModel();
    const cmpVisible = false;

    cmpApi.update(tcString, cmpVisible);
  }
}

/**
 * Toggle between new / first time and update / chagne consent event.
 */
export function triggerCmpOpenEvent() {
  const tcStringCookie = getTCStringFromCookie();
  const event = tcStringCookie ? CMP_EVENTS.UPDATE : CMP_EVENTS.NEW;
  triggerCmpEvent(event);
}

/**
 * Clear all consents from the TC Model and tell the cmpApi
 * to update as if the consent UI is showing for the very first time.
 */
export function resetAll() {
  const tcModel = getTCModel();

  if (!tcModel) {
    return;
  }

  clearTCStringCookie();

  tcModel.vendorConsents.empty();
  tcModel.purposeConsents.empty();
  tcModel.purposeLegitimateInterests.empty();
  tcModel.specialFeatureOptins.empty();
}

export function clearTCStringCookie() {
  document.cookie = `${TC_STRING_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/**
 * Cookie used for page reload persistence, so the TC String can be
 * set to the TC Model, which powers state for IAB TCF vendors, purposes, features.
 */
export function modifyTCStringCookie() {
  const tcString = generateTCStringFromModel();

  document.cookie = `${TC_STRING_COOKIE_NAME}=${tcString}; path=/; expires=${new Date(
    Date.now() + 7 * 24 * 60 * 60 * 1000
  ).toUTCString()}`;
}

export function getTCStringFromCookie() {
  const tcStringCookie = document.cookie.split('; ').find(row => row.startsWith(`${TC_STRING_COOKIE_NAME}=`));
  if (!tcStringCookie) {
    return null;
  }

  return tcStringCookie.split('=')[1];
}

/**
 * Helper to debug
 */
function registerCmpEventListener() {
  // client side only
  if (typeof window === 'undefined') {
    return;
  }

  if (!window.__tcfapi) {
    console.error('MC CMP - Cannot registerCmpEventListener, window.__tcfapi not found');
    return;
  }

  return window.__tcfapi('addEventListener', TCF_API_VERSION, (tcData, success) => {
    let allPurposeConsentVendorIds = [];

    _cmpEventListenerId = tcData?.listenerId;

    // note that third partys usually do not expose GVL, doing this just for easy testing
    // vendor consent = vendor opt in + all vendor declared purposes and legitimate interests
    const gvl = getGVL();

    if (success) {
      const consentVendorIds = Object.keys(tcData.vendor.consents).filter(
        vendorId => tcData.vendor.consents[vendorId] === true
      );

      const consentLegIntVendorIds = Object.keys(tcData.vendor.legitimateInterests).filter(
        vendorId => tcData.vendor.legitimateInterests[vendorId] === true
      );

      // make unique
      const combinedConsentVendorIds = Array.from(new Set([...consentVendorIds, ...consentLegIntVendorIds]));

      // check each vendor for opted in purposes and legint purposes
      allPurposeConsentVendorIds = combinedConsentVendorIds.filter(vendorId => {
        if (!gvl || !gvl.vendors || !gvl.vendors[vendorId]) {
          return false;
        }

        const vendor = gvl.vendors[vendorId];

        // all purposes must be opted in
        const purposesOptedIn = vendor.purposes.every(
          purposeId => Boolean(tcData.purpose.consents[purposeId]) === true
        );
        const legIntPurposesOptedIn = vendor.legIntPurposes.every(
          purposeId =>
            Boolean(tcData.purpose.consents[purposeId]) === true ||
            Boolean(tcData.purpose.legitimateInterests[purposeId]) === true
        );
        const specialPurposesOptedIn = vendor.specialPurposes.every(
          purposeId =>
            Boolean(tcData.purpose.consents[purposeId]) === true ||
            Boolean(tcData.purpose.legitimateInterests[purposeId]) === true
        );

        return purposesOptedIn && legIntPurposesOptedIn && specialPurposesOptedIn;
      });

      // consent cookie
      const consentCookie = getConsentCookie();

      console.log('MR CMP - window.__tcfApi event', {
        consentCookie: decodeURIComponent(consentCookie || ''),
        tcEvent: tcData.eventStatus,
        tcData,
        tcString: tcData.tcString,
        success,
        allPurposeConsentVendorIds,
      });
    }
  });
}

function removeCmpEventListener(listenerId) {
  // client side only
  if (typeof window === 'undefined') {
    return;
  }

  if (!window.__tcfapi) {
    console.error('MC CMP - Cannot removeCmpEventListener, window.__tcfapi');
    return;
  }

  // do not care if this fails
  const emptyCallback = () => {
    // noop
  };

  window.__tcfapi('removeEventListener', TCF_API_VERSION, emptyCallback, listenerId);
}
